/**
 * Shared utilities and functions for the Post-Haste Consulting Group components
 */

/**
 * Generates embed code for the Post-Haste Consulting Group section
 * This function directly generates the complete HTML rather than a script loader
 *
 * @param {Object} config - Configuration object with styling parameters
 * @returns {string} - Complete HTML code
 */
export const generatePHCGEmbedCode = (config) => {
  const {
    backgroundColor = "#1C436A",
    textColor = "#ffffff",
    headingFont = "serif",
    bodyFont = "sans-serif",
    buttonColor = "#CE950F",
    buttonText,
    buttonRadius = 4,
    buttonUrl = "https://PHCG-associates.com",
    companyName = "Post-Haste Consulting Group",
    companyAcronym = "PHCG",
  } = config || {};

  // Use dynamic button text if not provided
  const dynamicButtonText =
    buttonText || `More About ${companyName} (${companyAcronym})`;

  // Return the full HTML structure matching preview exactly
  return `<div style="
    background-color: ${backgroundColor};
    color: ${textColor};
    padding: 3rem 2rem;
    font-family: ${bodyFont};
    box-sizing: border-box;
    width: 100%;
  ">
  <div style="max-width: 800px; margin: 0 auto;">
    <!-- Title Section -->
    <div style="margin-bottom: 3rem;">
      <div style="
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: 0.75rem;
        margin-bottom: 1rem;
        display: block;
        opacity: 0.9;
        font-family: ${bodyFont};
      ">
        ABOUT US
      </div>

      <h2 style="
        font-family: ${headingFont};
        color: ${textColor};
        line-height: 1.2;
        margin: 0 0 2rem 0;
        font-size: 2.5rem;
        font-weight: normal;
      ">
        ${companyName}
      </h2>
    </div>

    <!-- Content Section -->
    <div style="max-width: 600px;">
      <p style="
        font-size: 0.95rem;
        line-height: 1.6;
        margin-bottom: 2rem;
        opacity: 0.95;
        font-family: ${bodyFont};
      ">
        ${companyName} has been working with family and non-family
        business partners for years. Among our services to partners is the
        Partnership Charter (PC). We use the online Design Your Partnership TM
        tool not only because it's the best structured program for helping
        partners develop a comprehensive document that covers both the
        interpersonal and business sides of their relationship, but it's also
        the world's only such tool! Our professionals have been trained in
        the use of the PC methodology and are licensed by The Partnership
        Charter Institute to use the DYP tool so we can help you with your
        business partnership.
      </p>

      <p style="
        font-size: 0.95rem;
        line-height: 1.6;
        margin-bottom: 3rem;
        opacity: 0.95;
        font-family: ${bodyFont};
      ">
        ${companyAcronym} used that information to begin focusing on conflict prevention
        among partners. Since around 2000, ${companyAcronym} has offered two services to
        partners: conflict resolution and conflict prevention.
      </p>

      <a href="${buttonUrl}" style="
        background-color: ${buttonColor};
        color: #ffffff;
        padding: 1rem 2rem;
        border-radius: ${buttonRadius}px;
        text-decoration: none;
        display: inline-block;
        font-size: 0.9rem;
        font-family: ${bodyFont};
        text-transform: none;
      ">
        ${dynamicButtonText}
      </a>
    </div>
  </div>
  
  <style>
    @media (max-width: 768px) {
      .phcg-container {
        padding: 2rem 1rem !important;
      }
    }
  </style>
</div>`;
};

/**
 * Default configuration for the PHCG Section
 */
export const defaultPHCGConfig = {
  backgroundColor: "#1C436A",
  textColor: "#ffffff",
  headingFont: "serif",
  bodyFont: "sans-serif",
  buttonColor: "#CE950F",
  buttonText: null, // Will use dynamic text
  buttonRadius: 4,
  buttonUrl: "https://PHCG-associates.com",
  companyName: "Post-Haste Consulting Group",
  companyAcronym: "PHCG",
};

/**
 * Alternative method to generate embed code using CSS classes and a separate style tag
 * This approach may be more maintainable for some websites
 *
 * @param {Object} config - Configuration object with styling parameters
 * @returns {string} - HTML with separate CSS
 */
export const generatePHCGEmbedCodeWithCSS = (config) => {
  const {
    backgroundColor = "#1C436A",
    textColor = "#ffffff",
    headingFont = "serif",
    bodyFont = "sans-serif",
    buttonColor = "#CE950F",
    buttonText,
    buttonRadius = 4,
    buttonUrl = "https://PHCG-associates.com",
    companyName = "Post-Haste Consulting Group",
    companyAcronym = "PHCG",
  } = config || {};

  // Use dynamic button text if not provided
  const dynamicButtonText =
    buttonText || `More About ${companyName} (${companyAcronym})`;

  return `<!-- PHCG Section Styles -->
<style>
  .phcg-container {
    background-color: ${backgroundColor};
    color: ${textColor};
    padding: 3rem 2rem 5rem;
    font-family: ${bodyFont};
    width: 100%;
    box-sizing: border-box;
  }
  
  .phcg-content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .phcg-subtitle {
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
  
  .phcg-heading {
    font-family: ${headingFont};
    color: ${textColor};
    font-size: 2.5rem;
    line-height: 1.2;
    margin: 0 0 2rem 0;
    font-weight: normal;
  }
  
  .phcg-content {
    max-width: 800px;
  }
  
  .phcg-paragraph {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  .phcg-button {
    background-color: ${buttonColor};
    color: #ffffff;
    padding: 0.75rem 1.5rem;
    border-radius: ${buttonRadius}px;
    text-decoration: none;
    display: inline-block;
    font-size: 1rem;
    font-family: ${bodyFont};
  }
  
  .phcg-button:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    .phcg-container {
      padding: 2rem 1.5rem 3rem;
    }
    
    .phcg-heading {
      font-size: 2rem;
    }
  }
</style>

<!-- PHCG Section HTML -->
<div class="phcg-container">
  <div class="phcg-content-wrapper">
    <h2 class="phcg-heading">${companyName}</h2>
    
    <div class="phcg-content">
      <p class="phcg-paragraph">
        ${companyName} has been working with family and non-family
        business partners for years. Among our services to partners is the
        Partnership Charter (PC). We use the online Design Your Partnership TM
        tool not only because it's the best structured program for helping
        partners develop a comprehensive document that covers both the
        interpersonal and business sides of their relationship, but it's also
        the world's only such tool! Our professionals have been trained in
        the use of the PC methodology and are licensed by The Partnership
        Charter Institute to use the DYP tool so we can help you with your
        business partnership.
      </p>
      
      <p class="phcg-paragraph">
        ${companyAcronym} used that information to begin focusing on conflict prevention
        among partners. Since around 2000, ${companyAcronym} has offered two services to
        partners: conflict resolution and conflict prevention.
      </p>
      
      <a href="${buttonUrl}" class="phcg-button">${dynamicButtonText}</a>
    </div>
  </div>
</div>`;
};
